"use client";

import React, { useState } from "react";
import styles from "./location-overview.module.css";

export type MarketInsight = {
  title: string;
  text: string;
  stat?: string; // NEW: Optional high-impact data point
};

export type MarketCategory = {
  id: string;
  label: string;
  lead: string;
  insights: MarketInsight[];
};

export type LocationOverviewData = {
  eyebrow: string;
  title: string;
  categories: MarketCategory[];
};

type LocationOverviewProps = {
  isGlobal: boolean;
  marketName: string;
  locale: string;
  overview?: LocationOverviewData;
};

function getMockOverview({
  isGlobal,
  marketName,
  locale,
}: {
  isGlobal: boolean;
  marketName: string;
  locale: string;
}): LocationOverviewData {
  // ==========================================
  // GLOBAL MARKET DATA
  // ==========================================
  if (isGlobal) {
    return {
      eyebrow: "Global Intelligence Architecture",
      title: "Real estate, shaped by the places people choose.",
      categories: [
        {
          id: "cat_wealth",
          label: "Global Wealth Hubs",
          lead: "Ultra-prime markets where capital preservation and transactional velocity converge on a massive scale.",
          insights: [
            {
              title: "Market Liquidity",
              text: "Tier-1 global cities continue to capture the highest share of cross-border institutional and private capital.",
            },
            {
              title: "Safe Haven Assets",
              text: "Trophy assets in legacy hubs maintain compounding value despite broader global macroeconomic volatility.",
            },
          ],
        },
        {
          id: "cat_resort",
          label: "Resort & Lifestyle",
          lead: "Driven by remote work flexibility, luxury secondary homes have fully transitioned into primary asset classes.",
          insights: [
            {
              title: "Alpine & Coastal Shift",
              text: "Demand for properties with immediate access to nature has outpaced urban core growth for consecutive quarters.",
            },
            {
              title: "Hybrid Yields",
              text: "Owners are increasingly leveraging short-term rental models during peak seasons to optimize carrying costs.",
            },
          ],
        },
        {
          id: "cat_emerging",
          label: "Emerging Corridors",
          lead: "Secondary cities are transforming into primary wealth destinations driven by tech infrastructure and tax incentives.",
          insights: [
            {
              title: "Demographic Pull",
              text: "Favorable tax structures are pulling young, high-net-worth demographics away from traditional megacities.",
            },
            {
              title: "Infrastructure ROI",
              text: "Areas with newly completed high-speed rail or expanded international airports are seeing immediate equity spikes.",
            },
          ],
        },
        {
          id: "cat_emerging",
          label: "Emerging Corridors",
          lead: "Secondary cities are transforming into primary wealth destinations driven by tech infrastructure and tax incentives.",
          insights: [
            {
              title: "Demographic Pull",
              text: "Favorable tax structures are pulling young, high-net-worth demographics away from traditional megacities.",
            },
            {
              title: "Infrastructure ROI",
              text: "Areas with newly completed high-speed rail or expanded international airports are seeing immediate equity spikes.",
            },
          ],
        },
      ],
    };
  }

  // ==========================================
  // LOCAL MARKET DATA (e.g., Dubai, Spain, etc.)
  // ==========================================
  return {
    eyebrow: `Market Profile // ${marketName}`,
    title: `A territory defined by its micro-pockets.`,
    categories: [
      {
        id: "cat_metro",
        label: "Metropolitan Speed",
        lead: "The primary urban engines command the highest transaction volume, structurally anchored by commercial expansion.",
        insights: [
          {
            title: "Corporate Migration",
            text: "High-density residential towers are seeing increased demand driven by steady domestic and international corporate relocations.",
          },
          {
            title: "Liquidity Cycles",
            text: "Urban assets offer the highest liquidity, with time-on-market averaging significantly lower than the national baseline.",
          },
        ],
      },
      {
        id: "cat_historic",
        label: "Historic Pockets",
        lead: "Where historical preservation and limited inventory create a premium on exclusivity over pure square footage.",
        insights: [
          {
            title: "Heritage Premiums",
            text: "Properties with verified historical architectural features maintain value regardless of broader macroeconomic cooling.",
          },
        ],
      },
      {
        id: "cat_coastal",
        label: "Coastal Luxury",
        lead: "Highly seasonal, culture-driven waterfront pockets where scenic topography carries priority over daily commuter access.",
        insights: [
          {
            title: "Seasonal Yields",
            text: "Short-term rental yields peak dramatically in Q2 and Q3, offering hybrid investment opportunities for part-time residents.",
          },
          {
            title: "Environmental Regulations",
            text: "Strict new coastal zoning laws have heavily capped new developments, accelerating appreciation on existing prime inventory.",
          },
          {
            title: "Buyer Demographics",
            text: "The majority of waterfront acquisitions are currently driven by out-of-state or international wealth preservation strategies.",
          },
        ],
      },
    ],
  };
}

export default function LocationOverview({
  isGlobal,
  marketName,
  locale,
  overview,
}: LocationOverviewProps) {
  const content = overview ?? getMockOverview({ isGlobal, marketName, locale });

  const [activeIndex, setActiveIndex] = useState(0);
  const activeCategory = content.categories[activeIndex];

  return (
    <section className={styles.section}>
      <div className={styles.canvas}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>{content.eyebrow}</span>
          <h2 className={styles.title}>{content.title}</h2>
        </header>

        <div className={styles.landscapeGrid}>
          {/* Column 1: Interactive Taxonomy Navigation */}
          <div className={styles.dnaColumn}>
            <div className={styles.stickyTaxonomy}>
              <span className={styles.columnLabel}>Select Market Focus</span>
              <div className={styles.tagCloud} role="tablist">
                {content.categories.map((cat, index) => {
                  const isActive = index === activeIndex;
                  return (
                    <button
                      key={cat.id}
                      role="tab"
                      aria-selected={isActive}
                      className={`${styles.dnaItem} ${isActive ? styles.activeDnaItem : ""}`}
                      onClick={() => setActiveIndex(index)}
                    >
                      <span className={styles.indexCode}>// 0{index + 1}</span>
                      <span className={styles.dnaText}>{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Columns 2 & 3: Dynamic Narrative Canvas */}
          <div key={activeCategory.id} className={styles.narrativeContent}>
            <div className={styles.leadContainer}>
              <span className={styles.activeLabelIndicator}>
                {activeCategory.label} Overview
              </span>
              <p className={styles.leadText}>{activeCategory.lead}</p>
            </div>

            <div className={styles.insightsTrack}>
              {activeCategory.insights.map((insight, idx) => (
                <div key={idx} className={styles.insightBlock}>
                  <div className={styles.insightHeader}>
                    <span className={styles.insightMarker}>
                      Insight_0{idx + 1}
                    </span>
                    <h3 className={styles.insightTitle}>{insight.title}</h3>
                  </div>
                  <p className={styles.insightBody}>{insight.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
